import React, { useState, useEffect } from 'react';
import { X } from 'react-feather';
import { useCalendar } from '../contexts/CalendarContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../hooks/useConfirm';
import { useResponsive } from '../hooks/useResponsive';
import Modal from './Modal';
import { TextInput, TextArea } from './ui';

/* The create/edit form repeated the same label and the same box five times.
 * Neither carries `outline`: inline, it outranks the global :focus-visible ring
 * in globals.css and every field in this modal drew nothing on keyboard focus. */
const eventLabelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '8px',
  fontSize: '13px',
  fontWeight: '600',
  color: '#151133',
};

const eventControlStyle: React.CSSProperties = {
  padding: '12px 16px',
  border: '1px solid #dee2e6',
  borderRadius: '8px',
  fontSize: '14px',
};

const COLOR_PALETTE = [
  { name: 'Orange', hex: '#f9550d' },
  { name: 'Blue', hex: '#3b82f6' },
  { name: 'Purple', hex: '#8b5cf6' },
  { name: 'Pink', hex: '#ec4899' },
  { name: 'Red', hex: '#ef4444' },
  { name: 'Green', hex: '#10b981' },
  { name: 'Yellow', hex: '#f59e0b' },
  { name: 'Gray', hex: '#6b7280' }
];

interface CalendarEvent {
  id: number;
  title: string;
  description?: string;
  start_date: string;
  end_date: string;
  color: string;
  created_by: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  event?: CalendarEvent | null;
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  canEdit: boolean;
}

const EventModal: React.FC<EventModalProps> = ({ 
  isOpen, 
  onClose, 
  event, 
  isEditing, 
  setIsEditing,
  canEdit 
}) => {
  const { createEvent, updateEvent, deleteEvent } = useCalendar();
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const { ConfirmComponent: DeleteConfirm, confirm: confirmDelete } = useConfirm({
    title: 'Delete Event',
    message: 'Are you sure you want to delete this event? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    variant: 'danger',
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    color: '#f9550d'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title,
        description: event.description || '',
        // <input type="date"> requires a bare YYYY-MM-DD; feeding a raw ISO
        // timestamp leaves the input blank (audit #197).
        start_date: event.start_date ? event.start_date.split('T')[0] : '',
        end_date: event.end_date ? event.end_date.split('T')[0] : '',
        color: event.color
      });
    } else {
      setFormData({ 
        title: '', 
        description: '', 
        start_date: '', 
        end_date: '', 
        color: '#f9550d' 
      });
    }
  }, [event]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter an event title');
      return;
    }
    
    if (!formData.start_date || !formData.end_date) {
      toast.error('Please select start and end dates');
      return;
    }
    
    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      toast.error('End date cannot be before start date');
      return;
    }

    // Guard the no-op path (audit #180): CalendarContext's create/update early-return
    // (no throw) when there's no session, which would otherwise fall through to the
    // "success" toast below even though nothing was saved.
    if (!sessionToken) {
      toast.error('Your session has expired. Please sign in again.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (event) {
        await updateEvent(event.id, formData);
        toast.success('Event updated successfully');
      } else {
        await createEvent(formData);
        toast.success('Event created successfully');
      }
      onClose();
    } catch (error) {
      toast.error(event ? 'Failed to update event' : 'Failed to create event');
      console.error('Event save error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!event) return;
    
    const confirmed = await confirmDelete();
    
    if (confirmed) {
      try {
        await deleteEvent(event.id);
        toast.success('Event deleted successfully');
        onClose();
      } catch (error) {
        toast.error('Failed to delete event');
        console.error('Event delete error:', error);
      }
    }
  };

  if (!isOpen) return null;

  // View mode vs Edit mode
  const viewMode = event && !isEditing;

  // Dirty tracking for the shared Modal's discard guard (audit #223) — only when
  // creating/editing, so a backdrop click or Escape prompts "Discard changes?".
  const isDirty = !viewMode && (
    event
      ? (
          formData.title !== event.title ||
          formData.description !== (event.description || '') ||
          formData.start_date !== (event.start_date ? event.start_date.split('T')[0] : '') ||
          formData.end_date !== (event.end_date ? event.end_date.split('T')[0] : '') ||
          formData.color !== event.color
        )
      : (
          formData.title.trim() !== '' ||
          formData.description.trim() !== '' ||
          formData.start_date !== '' ||
          formData.end_date !== ''
        )
  );

  return (
    <>
      <DeleteConfirm />
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isDirty={isDirty}
        labelledById="event-modal-title"
        zIndex={9999}
        contentStyle={{
          width: isMobile ? 'calc(100vw - 32px)' : '100%',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '600px',
          maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'Manrope, sans-serif',
        }}
      >
        {/* Header */}
        <div style={{
          padding: isMobile ? '16px' : '24px 32px',
          borderBottom: '1px solid #e9ecef',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h3 id="event-modal-title" style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '700',
            color: '#151133'
          }}>
            {event ? (viewMode ? 'Event Details' : 'Edit Event') : 'New Event'}
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              border: 'none',
              background: '#f8f9fa',
              color: '#6c757d',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#e9ecef';
              e.currentTarget.style.color = '#151133';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f8f9fa';
              e.currentTarget.style.color = '#6c757d';
            }}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div style={{
          flex: 1,
          padding: '32px',
          overflow: 'auto'
        }}>
          {viewMode ? (
            // View Mode
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '6px',
                    backgroundColor: event.color,
                    flexShrink: 0
                  }}
                />
                <h4 style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#151133'
                }}>
                  {event.title}
                </h4>
              </div>

              {event.description && (
                <div>
                  <p style={{
                    margin: '0 0 4px 0',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Description
                  </p>
                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    color: '#495057',
                    lineHeight: '1.6'
                  }}>
                    {event.description}
                  </p>
                </div>
              )}

              <div>
                <p style={{
                  margin: '0 0 4px 0',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6c757d',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Dates
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#495057'
                }}>
                  {new Date(event.start_date.split('T')[0] + 'T00:00:00').toLocaleDateString('en-US', { 
                    month: 'long', 
                    day: 'numeric', 
                    year: 'numeric' 
                  })}
                  {event.start_date !== event.end_date && (
                    <> to {new Date(event.end_date.split('T')[0] + 'T00:00:00').toLocaleDateString('en-US', { 
                      month: 'long', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}</>
                  )}
                </p>
              </div>

              {event.created_by_name && (
                <div>
                  <p style={{
                    margin: '0 0 4px 0',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Created By
                  </p>
                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    color: '#495057'
                  }}>
                    {event.created_by_name}
                  </p>
                </div>
              )}
            </div>
          ) : (
            // Edit/Create Mode
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Four hand-styled boxes became three primitives and a
                  TextArea. Each carried an inline `outline: none` plus an
                  onFocus/onBlur border swap; the swap only ever fired for a
                  mouse, so a keyboard user saw nothing. The global
                  :focus-visible ring is the indicator now. The border itself
                  stops tinting: `.aps-input:focus` would do it, but the
                  primitive's inline `border` outranks it (see globals.css). */}
              <div>
                <label htmlFor="event-title" style={eventLabelStyle}>
                  Event Title *
                </label>
                <TextInput
                  id="event-title"
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., College Showcase"
                  style={eventControlStyle}
                />
              </div>

              <div>
                <label htmlFor="event-description" style={eventLabelStyle}>
                  Description
                </label>
                <TextArea
                  id="event-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Add details about this event..."
                  rows={4}
                  style={eventControlStyle}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                <div>
                  <label htmlFor="event-start-date" style={eventLabelStyle}>
                    Start Date *
                  </label>
                  <TextInput
                    id="event-start-date"
                    type="date"
                    required
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    style={{
                      ...eventControlStyle,
                      maxWidth: '100%',
                      /* 16px on a phone, because iOS zooms the page in on any
                       * focused control below that. */
                      fontSize: isMobile ? '16px' : '14px',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      appearance: 'none'
                    }}
                  />
                </div>

                <div>
                  <label htmlFor="event-end-date" style={eventLabelStyle}>
                    End Date *
                  </label>
                  <TextInput
                    id="event-end-date"
                    type="date"
                    required
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    style={{
                      ...eventControlStyle,
                      maxWidth: '100%',
                      fontSize: isMobile ? '16px' : '14px',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      appearance: 'none'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '12px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#151133'
                }}>
                  Color
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(8, 1fr)',
                  gap: '12px'
                }}>
                  {COLOR_PALETTE.map(colorOption => (
                    <div
                      key={colorOption.hex}
                      onClick={() => setFormData({ ...formData, color: colorOption.hex })}
                      style={{
                        width: '100%',
                        paddingTop: '100%',
                        position: 'relative',
                        cursor: 'pointer',
                        borderRadius: '8px',
                        backgroundColor: colorOption.hex,
                        border: formData.color === colorOption.hex ? '3px solid #151133' : '2px solid #e9ecef',
                        transition: 'all 0.2s ease',
                        boxShadow: formData.color === colorOption.hex ? '0 2px 8px rgba(0, 0, 0, 0.2)' : 'none'
                      }}
                      title={colorOption.name}
                    />
                  ))}
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 32px',
          borderTop: '1px solid #e9ecef',
          display: 'flex',
          justifyContent: viewMode ? 'space-between' : 'flex-end',
          gap: '12px'
        }}>
          {viewMode ? (
            <>
              {canEdit && (
                <button
                  onClick={handleDelete}
                  style={{
                    padding: '10px 20px',
                    background: '#fff',
                    color: '#dc3545',
                    border: '1px solid #dc3545',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Manrope, sans-serif'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#dc3545';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#fff';
                    e.currentTarget.style.color = '#dc3545';
                  }}
                >
                  Delete
                </button>
              )}
              <div style={{ display: 'flex', gap: '12px' }}>
                {canEdit && (
                  <button
                    onClick={() => setIsEditing(true)}
                    style={{
                      padding: '10px 20px',
                      background: 'linear-gradient(135deg, #f9550d 0%, #e64a0c 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                      boxShadow: '0 2px 8px rgba(249, 85, 13, 0.2)',
                      fontFamily: 'Manrope, sans-serif'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    Edit
                  </button>
                )}
                <button
                  onClick={onClose}
                  style={{
                    padding: '10px 20px',
                    background: '#f8f9fa',
                    color: '#6c757d',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease',
                    fontFamily: 'Manrope, sans-serif'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#e9ecef'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#f8f9fa'}
                >
                  Close
                </button>
              </div>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                style={{
                  padding: '10px 20px',
                  background: '#f8f9fa',
                  color: '#6c757d',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.5 : 1,
                  transition: 'background 0.2s ease',
                  fontFamily: 'Manrope, sans-serif'
                }}
                onMouseEnter={(e) => !isSubmitting && (e.currentTarget.style.background = '#e9ecef')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '#f8f9fa')}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                style={{
                  padding: '10px 20px',
                  background: isSubmitting 
                    ? '#ccc' 
                    : 'linear-gradient(135deg, #f9550d 0%, #e64a0c 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'transform 0.2s ease',
                  boxShadow: isSubmitting ? 'none' : '0 2px 8px rgba(249, 85, 13, 0.2)',
                  fontFamily: 'Manrope, sans-serif'
                }}
                onMouseEnter={(e) => !isSubmitting && (e.currentTarget.style.transform = 'translateY(-1px)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
              >
                {isSubmitting ? 'Saving...' : 'Save Event'}
              </button>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default EventModal;
